import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";

/**
 * Canonical className for numeric / count / currency / duration content.
 * Right-aligns with tabular figures per the alignment standard
 * (docs/claude/text-alignment.md). Use it on both the header and the cells so
 * the column stays consistent — in raw tables put it on `<TableHead>` and
 * `<TableCell>`, or in TanStack tables prefer `numericColumn()` below.
 *
 * Lives in a non-component module (not data-table-utils.tsx) so the
 * react-refresh/only-export-components rule stays satisfied.
 */
export const NUMERIC_CELL_CLASS = "text-right tabular-nums";

/**
 * Wrap a TanStack column definition so its cells (via `meta.className`) follow
 * the numeric alignment standard, without re-typing `text-right` per column.
 * If the column already uses a function-based `meta.className`, it's left
 * untouched so the caller keeps control.
 */
export function numericColumn<TData, TValue>(
  column: ColumnDef<TData, TValue>,
): ColumnDef<TData, TValue> {
  const existing = column.meta?.className;
  if (typeof existing === "function") return column;
  return {
    ...column,
    meta: { ...column.meta, className: cn(NUMERIC_CELL_CLASS, existing) },
  };
}
